/**
 * Basketball Ask SLAM AI
 *
 * Unified Ask SLAM AI surface for basketball.
 * Uses the shared AskSLAMAI chat/canvas experience with basketball prompts.
 * Prompts are dynamically personalized based on the selected team.
 */

import React, { useState, useEffect, useMemo } from 'react';
import AskSLAMAI from '../../components/chat/AskSLAMAI';
import { useTeam } from '../../lib/teamConfig';

// Prompt templates — {player} and {team} are replaced dynamically
const PROMPT_TEMPLATES = [
  { id: 1, text: "What are {player}'s stats this season?", icon: '📊' },
  { id: 2, text: 'Show the {team} roster and top performers', icon: '🏀' },
  { id: 3, text: 'How does {player} compare to league leaders?', icon: '📈' },
  { id: 4, text: 'Latest news and updates for the {team}', icon: '📰' },
  { id: 5, text: '{team} offensive and defensive rankings', icon: '🎯' },
  { id: 6, text: "Analyze {player}'s efficiency and shooting splits", icon: '🔥' },
];

// Fallback prompts when no team is selected
const GENERIC_PROMPTS = [
  { id: 1, text: 'Who are the top scorers in the NBA this season?', icon: '📊' },
  { id: 2, text: 'Which team has the best record right now?', icon: '🏀' },
  { id: 3, text: 'Compare the top 5 assist leaders in the league', icon: '📈' },
  { id: 4, text: 'Latest NBA news and trade rumors', icon: '📰' },
  { id: 5, text: 'Best defensive teams in the league this season', icon: '🎯' },
  { id: 6, text: 'Which rookies are having the biggest impact?', icon: '🔥' },
];

export default function BasketballAskAI() {
  const { selectedTeam } = useTeam();
  const [topPlayers, setTopPlayers] = useState([]);

  // NatStat uses team_name (city, e.g. "Atlanta") and full_name (e.g. "Atlanta Hawks")
  const teamDisplayName = selectedTeam?.team?.full_name || selectedTeam?.team?.team_name || selectedTeam?.teamName || null;
  const teamSearchName = selectedTeam?.team?.team_name || selectedTeam?.teamName || null;
  const leagueCode = selectedTeam?.leagueCode || selectedTeam?.league?.code || selectedTeam?.sport?.code || 'NBA';

  // Fetch top players for the selected team
  useEffect(() => {
    if (!teamSearchName) {
      setTopPlayers([]);
      return;
    }

    let cancelled = false;
    const fetchTopPlayers = async () => {
      try {
        const params = new URLSearchParams({ league: leagueCode, team: teamSearchName, limit: '5' });
        const res = await fetch(`/api/basketball/top-players?${params}`);
        if (!res.ok) return;
        const json = await res.json();
        // respond() wrapper puts actual payload inside json.data
        const payload = json.data || json;
        if (!cancelled && payload.players?.length > 0) {
          setTopPlayers(payload.players);
        }
      } catch (e) {
        console.warn('[BasketballAskAI] Failed to fetch top players:', e.message);
      }
    };
    fetchTopPlayers();
    return () => { cancelled = true; };
  }, [teamSearchName, leagueCode]);

  // Build personalized prompts
  const prompts = useMemo(() => {
    if (!teamDisplayName) return GENERIC_PROMPTS;

    // Pick top player by PPG for prompt personalization
    const starPlayer = topPlayers[0]?.player_name || null;
    if (!starPlayer) {
      // Team selected but players haven't loaded yet — use team-only prompts
      return PROMPT_TEMPLATES.map(p => ({
        ...p,
        text: p.text.replace(/\{player\}/g, 'the top scorer').replace(/\{team\}/g, teamDisplayName),
      }));
    }

    // Use different players across prompts for variety
    const players = topPlayers.map(p => p.player_name);
    return PROMPT_TEMPLATES.map((p, i) => ({
      ...p,
      text: p.text
        .replace(/\{player\}/g, players[i % players.length])
        .replace(/\{team\}/g, teamDisplayName),
    }));
  }, [teamDisplayName, topPlayers]);

  return (
    <AskSLAMAI
      sport="basketball"
      prompts={prompts}
      endpoint="/api/chat/context/stream"
      accentColor="green"
    />
  );
}
